/** Convert degrees to radians. */
export function deg(degrees: number): number {
    return degrees * Math.PI / 180;
}

/** Half-extents (x, y, z) for each bone's box collider. */
export const BONE_HALF_EXTENTS: Readonly<Record<string, [number, number, number]>> = {
    Hips:          [0.12, 0.07, 0.07],
    Spine:         [0.14, 0.03, 0.07],
    Spine1:        [0.14, 0.03, 0.08],
    Spine2:        [0.14, 0.03, 0.08],
    Neck:          [0.04, 0.06, 0.04],
    Head:          [0.07, 0.09, 0.08],
    ForeArmLeft:   [0.04, 0.10, 0.04],
    ShoulderLeft:  [0.05, 0.04, 0.04],
    ArmLeft:       [0.04, 0.07, 0.04],
    HandLeft:      [0.04, 0.05, 0.03],
    ShoulderRight: [0.05, 0.04, 0.04],
    ArmRight:      [0.04, 0.07, 0.04],
    ForeArmRight:  [0.04, 0.10, 0.04],
    HandRight:     [0.04, 0.05, 0.03],
    UpLegLeft:     [0.06, 0.1, 0.06],
    LegLeft:       [0.05, 0.14, 0.05],
    FootLeft:      [0.04, 0.1, 0.02],
    UpLegRight:    [0.06, 0.1, 0.06],
    LegRight:      [0.05, 0.14, 0.05],
    FootRight:     [0.04, 0.1, 0.02],
};

/** Local [x, y, z] offset applied on top of bone world position. */
export const BONE_OFFSET: Readonly<Partial<Record<string, [number, number, number]>>> = {
    Hips:          [0, -0.08, 0],
    Head:          [0, 0.07, 0.05],
    ArmLeft:       [0, 0.05, 0],
    ForeArmLeft:   [0, 0.08, 0],
    ShoulderLeft:  [0, 0.04, 0],
    HandLeft:      [0, 0.07, 0],
    ArmRight:      [0, 0.05, 0],
    ShoulderRight: [0, 0.04, 0],
    ForeArmRight:  [0, 0.08, 0],
    HandRight:     [0, 0.07, 0],
    UpLegLeft:     [0, 0.2, 0],
    LegLeft:       [0, 0.13, 0],
    FootLeft:      [0, 0.05, 0],
    UpLegRight:    [0, 0.2, 0],
    LegRight:      [0, 0.13, 0],
    FootRight:     [0, 0.05, 0],
};

/** Top-down hierarchical order for bone processing. */
export const PROCESS_ORDER: ReadonlyArray<string> = [
    'Hips',
    'Spine', 'Spine1', 'Spine2',
    'Neck', 'Head',
    'ShoulderLeft', 'ArmLeft', 'ForeArmLeft', 'HandLeft',
    'ShoulderRight', 'ArmRight', 'ForeArmRight', 'HandRight',
    'UpLegLeft', 'LegLeft', 'FootLeft',
    'UpLegRight', 'LegRight', 'FootRight',
];

/** [parentBone, childBone] hierarchy pairs for constraint building. */
export const BONE_HIERARCHY: ReadonlyArray<[string, string]> = [
    ['Hips', 'Spine'],
    ['Spine', 'Spine1'],
    ['Spine1', 'Spine2'],
    ['Spine2', 'Neck'],
    ['Neck', 'Head'],
    ['Spine2', 'ShoulderLeft'],
    ['ShoulderLeft', 'ArmLeft'],
    ['ArmLeft', 'ForeArmLeft'],
    ['ForeArmLeft', 'HandLeft'],
    ['Spine2', 'ShoulderRight'],
    ['ShoulderRight', 'ArmRight'],
    ['ArmRight', 'ForeArmRight'],
    ['ForeArmRight', 'HandRight'],
    ['Hips', 'UpLegLeft'],
    ['UpLegLeft', 'LegLeft'],
    ['LegLeft', 'FootLeft'],
    ['Hips', 'UpLegRight'],
    ['UpLegRight', 'LegRight'],
    ['LegRight', 'FootRight'],
];

/** Maps bone names to their joint constraint group. */
export const BONE_JOINT: Readonly<Record<string, string>> = {
    Neck: 'Neck',
    Head: 'Neck',
    Spine: 'Spine',
    Spine1: 'Spine',
    Spine2: 'Spine',
    ShoulderLeft: 'Shoulder',
    ShoulderRight: 'Shoulder',
    ArmLeft: 'UpperArm',
    ArmRight: 'UpperArm',
    ForeArmLeft: 'Elbow',
    ForeArmRight: 'Elbow',
    HandLeft: 'Wrist',
    HandRight: 'Wrist',
    UpLegLeft: 'Hip',
    UpLegRight: 'Hip',
    LegLeft: 'Knee',
    LegRight: 'Knee',
    FootLeft: 'Ankle',
    FootRight: 'Ankle',
};

/** Ragdoll bodies belong to their own collision group. */
export const RAGDOLL_GROUP = 8;

export const LINEAR_DAMPING = 0.9;
export const ANGULAR_DAMPING = 0.99;
export const CONE_ANGLE = Math.PI / 20;
// Leg bones have a non-zero twist at rest in the skeleton, so no twist limit is used to avoid
// an instant 180° flip on activation. All other joints use their own defined twistAngle.
export const LEG_TWIST_ANGLE = Math.PI;
// Softer than the Cannon default (1e7) to avoid solver over-correction, but stiff enough to
// hold joints near their limits. Raised from 1e6 after the pivot-at-joint fix removed shakiness.
export const CONSTRAINT_STIFFNESS = 5e6;
export const CONSTRAINT_RELAXATION = 4;

